import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../../utils/api';
import Card from '../common/Card';
import Button from '../common/Button';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import { formatCategory } from '../../utils/formatters';

const FeaturedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProjects = async () => {
      try {
        setLoading(true);
        const response = await projectsAPI.getFeatured();
        setProjects(response.data.data || []);
      } catch (err) {
        setError(err.message || 'Failed to load featured projects');
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProjects();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <Loading size="lg" text="Loading featured projects..." />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-bg-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage message={error} />
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 bg-bg-secondary">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with fade-in animation */}
        <div
          className="text-center mb-16 md:mb-20"
        >
          <h2 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
            Featured Work
          </h2>
          <p className="text-lg md:text-xl text-text-secondary font-body max-w-2xl mx-auto leading-relaxed">
            Explore a selection of our most captivating portraits and projects
          </p>
        </div>

        {projects.length === 0 ? (
          <p className="text-center text-text-primary font-body text-lg">
            No featured projects yet. Check back soon!
          </p>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {projects.map((project, index) => (
                <Link
                  key={project._id}
                  to={`/portfolio/${project.slug}`}
                >
                  <Card hover>
                    <div className="aspect-w-3 aspect-h-4 relative overflow-hidden">
                      <img
                        src={project.coverImage}
                        alt={project.title}
                        className="w-full object-cover transition-transform duration-600 hover:scale-110"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 lg:p-8">
                      <div className="text-sm text-accent-secondary font-body font-semibold uppercase tracking-wider mb-2">
                        {formatCategory(project.category)}
                      </div>
                      <h3 className="text-xl lg:text-2xl font-heading font-bold text-secondary mb-3 group-hover:text-accent-secondary transition-colors duration-400">
                        {project.title}
                      </h3>
                      <p className="text-text-secondary font-body leading-relaxed line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>

            {/* Button */}
            <div className="text-center mt-16">
              <Link to="/portfolio">
                <Button
                  variant="outline"
                  size="lg"
                  className="hover:bg-accent-secondary hover:text-white hover:border-accent-secondary transition-all duration-400"
                >
                  View All Projects
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedProjects;
